import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, ArrowLeft, Timer, Trophy, Sparkles } from "lucide-react";
import { playPop } from "@/lib/popSound";
import BubbleLogo from "@/components/BubbleLogo";

const GAME_SECONDS = 30;
const rand = (a, b) => Math.random() * (b - a) + a;

export default function BubblePop() {
  const [phase, setPhase] = useState("idle");
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [bubbles, setBubbles] = useState([]);
  const [best, setBest] = useState(() => Number(localStorage.getItem("bubz-best") || 0));
  const idRef = useRef(0);

  useEffect(() => {
    if (phase !== "playing") return;
    const spawn = setInterval(() => {
      const id = ++idRef.current;
      const golden = Math.random() < 0.14;
      setBubbles((b) => [
        ...b.slice(-9),
        { id, x: rand(3, 88), y: rand(4, 84), size: rand(46, 96), golden },
      ]);
      setTimeout(() => setBubbles((b) => b.filter((x) => x.id !== id)), 2400);
    }, 470);
    const tick = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => {
      clearInterval(spawn);
      clearInterval(tick);
    };
  }, [phase]);

  useEffect(() => {
    if (phase === "playing" && timeLeft === 0) setPhase("done");
  }, [timeLeft, phase]);

  useEffect(() => {
    if (phase === "done" && score > best) {
      setBest(score);
      localStorage.setItem("bubz-best", String(score));
    }
  }, [phase]); // eslint-disable-line

  const start = () => {
    setScore(0);
    setTimeLeft(GAME_SECONDS);
    setBubbles([]);
    setPhase("playing");
  };

  const pop = (id, golden) => {
    playPop();
    setScore((s) => s + (golden ? 5 : 1));
    setBubbles((b) => b.filter((x) => x.id !== id));
  };

  return (
    <main
      data-testid="game-page"
      className="min-h-screen pb-20 pt-32"
      style={{ background: "linear-gradient(180deg, #E0F2FE 0%, #F0FDF4 60%, #FFFBEB 100%)" }}
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="game-back-home"
            className="flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-extrabold text-bubz-ink shadow-sm transition-colors hover:bg-white"
          >
            <ArrowLeft size={15} /> Back to the park
          </Link>
          <span className="flex items-center gap-1.5 rounded-full bg-bubz-grape/15 px-4 py-2 text-sm font-extrabold text-bubz-ink">
            <Trophy size={15} className="text-bubz-grape" /> Best: <span data-testid="game-best">{best}</span>
          </span>
        </div>

        <div className="overflow-hidden rounded-[3rem] border-4 border-white bg-white/60 shadow-[0_24px_70px_rgba(112,193,255,0.3)] backdrop-blur">
          <div className="flex items-center justify-between gap-3 px-6 py-4">
            <span className="flex items-center gap-2 rounded-full bg-bubz-pink px-4 py-2 font-display text-lg font-semibold text-white">
              <Sparkles size={17} /> Score: <span data-testid="game-score">{score}</span>
            </span>
            <span
              className={`flex items-center gap-2 rounded-full px-4 py-2 font-display text-lg font-semibold text-white ${
                timeLeft <= 5 && phase === "playing" ? "animate-pulse bg-red-400" : "bg-bubz-sky"
              }`}
            >
              <Timer size={17} /> <span data-testid="game-timer">{timeLeft}s</span>
            </span>
          </div>

          <div
            data-testid="game-arena"
            className="relative mx-4 mb-4 h-[56vh] min-h-[380px] overflow-hidden rounded-[2.2rem] border-2 border-bubz-sky/30 sm:mx-6 sm:mb-6"
            style={{ background: "linear-gradient(180deg, #D8EFFF 0%, #EAF9EF 100%)" }}
          >
            {phase === "idle" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <BubbleLogo size={90} />
                </motion.div>
                <h1 className="mt-6 font-display text-3xl font-bold text-bubz-ink sm:text-4xl">
                  Bubble Pop Challenge
                </h1>
                <p className="mt-3 max-w-sm text-base font-semibold text-bubz-ink/60">
                  Pop as many bubbles as you can in 30 seconds! Golden bubbles are worth 5 points.
                </p>
                <motion.button
                  data-testid="game-start-button"
                  onClick={start}
                  className="mt-8 flex items-center gap-2.5 rounded-full bg-bubz-pink px-8 py-4 font-display text-xl font-semibold text-white shadow-[0_10px_30px_rgba(255,117,181,0.55)]"
                  whileHover={{ scale: 1.07, rotate: -1 }}
                  whileTap={{ scale: 0.94 }}
                >
                  <Play size={20} fill="currentColor" /> Start popping!
                </motion.button>
              </div>
            )}

            {phase === "playing" && (
              <AnimatePresence>
                {bubbles.map((b) => (
                  <motion.button
                    key={b.id}
                    type="button"
                    data-testid={`game-bubble-${b.id}`}
                    aria-label={b.golden ? "Golden bubble, 5 points" : "Bubble, 1 point"}
                    onClick={() => pop(b.id, b.golden)}
                    className={`bubble absolute cursor-pointer rounded-full ${
                      b.golden ? "ring-4 ring-bubz-sun" : ""
                    }`}
                    style={{
                      left: `${b.x}%`,
                      top: `${b.y}%`,
                      width: b.size,
                      height: b.size,
                      background: b.golden
                        ? "radial-gradient(circle at 30% 28%, rgba(255,255,255,0.98) 0%, rgba(255,240,180,0.5) 35%, rgba(255,208,52,0.55) 70%, rgba(255,140,66,0.5) 100%)"
                        : undefined,
                    }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1, y: [0, -10, 0] }}
                    exit={{ scale: [1, 1.6, 0], opacity: [1, 0.8, 0], transition: { duration: 0.3 } }}
                    transition={{
                      scale: { type: "spring", stiffness: 260, damping: 14 },
                      y: { duration: 1.6, repeat: Infinity, ease: "easeInOut" },
                    }}
                  />
                ))}
              </AnimatePresence>
            )}

            {phase === "done" && (
              <motion.div
                className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 160, damping: 16 }}
              >
                <p className="font-display text-lg font-semibold uppercase tracking-[0.25em] text-bubz-sky">
                  Time's up!
                </p>
                <p className="mt-2 font-display text-7xl font-bold text-bubz-ink" data-testid="game-final-score">
                  {score}
                </p>
                <p className="mt-1 text-base font-extrabold text-bubz-ink/60">
                  bubbles popped {score >= best && score > 0 && "— that's your new best!"}
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                  <motion.button
                    data-testid="game-play-again-button"
                    onClick={start}
                    className="flex items-center gap-2 rounded-full bg-bubz-grass px-7 py-3.5 font-display text-lg font-semibold text-white shadow-[0_8px_24px_rgba(76,217,100,0.5)]"
                    whileHover={{ scale: 1.06, rotate: 1 }}
                    whileTap={{ scale: 0.94 }}
                  >
                    <RotateCcw size={18} /> Play again
                  </motion.button>
                  <motion.span whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}>
                    <Link
                      to="/"
                      data-testid="game-done-back-home"
                      className="flex items-center gap-2 rounded-full border-[3px] border-bubz-ink bg-white px-7 py-3 font-display text-lg font-semibold text-bubz-ink"
                    >
                      <ArrowLeft size={18} /> More adventures
                    </Link>
                  </motion.span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
